import React, {useEffect, useRef, useState} from "react";
import {
    Container,
    Title,
    Table,
    Paper,
    Anchor,
    Text,
    TextInput,
    Group,
    Center,
    UnstyledButton,
    ScrollArea, Button,
} from "@mantine/core";
import { IconClockHour3, IconSignature, IconScan, IconMapPin, IconProgressAlert, IconUserFilled } from "@tabler/icons-react";
import { IconArrowLeft, IconChevronUp, IconChevronDown, IconSearch, IconSelector } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { customerHamperList } from "../../store/reducers/employee";
import Spinner from "../../components/Spinner/v1/Spinner";
import "./VerifiedCustomerHamper.css"
import {tracking} from "../../store/reducers/hamper";
import {nextStatusMapping, statusDisplay} from "../../components/Verification/HamperCodes";
import EmployeeCustomerTD from "../../components/Verification/Employee/EmployeeCustomerTD";

const LOCAL_STORAGE_KEY = "verifiedHamperSortState";

function Th({ children, reversed, sorted, onSort }) {
    const Icon = sorted ? (reversed ? IconChevronUp : IconChevronDown) : IconSelector;
    return (
        <Table.Th>
            <UnstyledButton onClick={onSort} style={{ width: "100%" }}>
                <Group justify="space-between">
                    <Text fw={500} fz="sm">
                        {children}
                    </Text>
                    <Center>
                        <Icon size={16} stroke={1.5} />
                    </Center>
                </Group>
            </UnstyledButton>
        </Table.Th>
    );
}

const VerifiedCustomerHamperList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const role = useSelector((state) => state.auth.role);
    const dashboardUri = `/${role?.toLowerCase()}`;
    const [loading, setLoading] = useState(false);
    const verifiedCustomerHamperList = useSelector((state) => state.employee.verifiedCustomerHamperList);

    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState(null);
    const [reverseSortDirection, setReverseSortDirection] = useState(false);
    const [sortedData, setSortedData] = useState([]);

    useEffect(() => {
        const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (saved) {
            const { search, sortBy, reverseSortDirection } = JSON.parse(saved);
            setSearch(search || "");
            setSortBy(sortBy || null);
            setReverseSortDirection(reverseSortDirection || false);
        }

        setLoading(true);
        dispatch(customerHamperList({ setLoading }));
    }, [dispatch]);

    useEffect( () => {
        if( sortedData?.length ) {
            scrollMessageFunctionality(true)
        }
        if( search ) {
            scrollMessageFunctionality(false)
        }
    }, [sortedData,search])

    function scrollMessageFunctionality( createListener ) {
        let displayText = document.querySelector(".auxiliaryCustomerScroll");
        const container = document.querySelector(".customerTableScrollArea .mantine-ScrollArea-viewport");

        if( !displayText ) {
            const scrollArea = document.querySelector(".customerTableScrollArea");
            displayText = document.createElement("div");
            displayText.classList.add("auxiliaryCustomerScroll");
            scrollArea?.append?.(displayText);
        }

        if( container ) {
            let isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;

            if (isAtBottom) {
                displayText.classList.add("at-bottom");
            } else {
                displayText.classList.remove("at-bottom");
            }
        }

        if( createListener ) {
            container?.addEventListener('scroll', function scrollFunctionality() {
                const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;

                if (isAtBottom) {
                    displayText.classList.add("at-bottom");
                } else {
                    displayText.classList.remove("at-bottom");
                }
            })
        }
    }

    useEffect(() => {
        if (verifiedCustomerHamperList.length > 0) {
            applySortFilter(search, sortBy, reverseSortDirection);
        }
    }, [verifiedCustomerHamperList, search, sortBy, reverseSortDirection]);

    const setSorting = (field) => {
        const reversed = field === sortBy ? !reverseSortDirection : false;
        setSortBy(field);
        setReverseSortDirection(reversed);
        localStorage.setItem(
            LOCAL_STORAGE_KEY,
            JSON.stringify({ sortBy: field, reverseSortDirection: reversed, search })
        );
    };

    const applySortFilter = (search, sortBy, reversed) => {
        let filtered = verifiedCustomerHamperList.filter((item) => {
            return (
                item.name.toLowerCase().includes(search.toLowerCase()) ||
                item.hamperCode.toLowerCase().includes(search.toLowerCase()) ||
                item.address.toLowerCase().includes(search.toLowerCase()) ||
                item.status.toLowerCase().includes(search.toLowerCase())
            );
        });

        if (sortBy) {
            filtered.sort((a, b) => {
                const aVal = a[sortBy] || "";
                const bVal = b[sortBy] || "";
                return reversed
                    ? bVal.toString().localeCompare(aVal.toString())
                    : aVal.toString().localeCompare(bVal.toString());
            });
        }

        setSortedData(filtered);
    };

    const handleSearchChange = (event) => {
        const value = event.currentTarget.value;
        setSearch(value);
        localStorage.setItem(
            LOCAL_STORAGE_KEY,
            JSON.stringify({ sortBy, reverseSortDirection, search: value })
        );
    };

    const handleStatusUpdate = (hamperCode, status) => {
        if (hamperCode && status) {
            const hamperList = JSON.parse( JSON.stringify(  verifiedCustomerHamperList ));
            const target = hamperList.find( item => item.hamperCode === hamperCode );

            if( target ) {
                target.status = status;
                const now = new Date();
                target.statusUpdatedAt = now.toISOString();
            }

            const body = {
                body: {
                    hamperCode,
                    status: status,
                },
                setHamperCode: null,
                setLoading,
                verifiedCustomerHamperList: hamperList,
            };
            setLoading(true);
            dispatch(tracking(body));
        }
    };

    const rows = sortedData.map((hamper, index) => {
        const status = statusDisplay[ hamper.status ];
        const pickupSkip = status === "Pickup Pending" || status === "Delivered"

        return (
            <Table.Tr key={index} className={"customerListTableRow"}>
                <EmployeeCustomerTD
                    hamper={hamper}
                    status={status}
                    pickupSkip={pickupSkip}
                    handleStatusUpdate={handleStatusUpdate}
                />
            </Table.Tr>
        );
    });

    return (
        <>
            {loading && <Spinner/>}
            <Container className={"customerListContainer"}>
                <Anchor
                    onClick={() => navigate(dashboardUri)}
                    c="blue"
                    size="sm"
                    mb="lg"
                    underline
                    className={"backToDashboard"}
                    style={{display: "inline-flex", alignItems: "center"}}
                >
                    <IconArrowLeft size={16} style={{marginRight: 8}}/>
                    Back to dashboard
                </Anchor>

                <Paper withBorder shadow="md" p={30} mt={30} mb={30} radius="md" className={"customerPaper"}>
                    <Title order={3} mb="md">
                        Customer Hamper List
                    </Title>

                    <TextInput
                        placeholder="Search by name, code, status, address"
                        mb="md"
                        leftSection={<IconSearch size={16} stroke={1.5} />}
                        value={search}
                        onChange={handleSearchChange}
                    />

                    {sortedData.length === 0 ? (
                        <Text size="sm" c="dimmed">
                            No hampers found.
                        </Text>
                    ) : (
                        <ScrollArea h={500} type="auto" className={"customerTableScrollArea"}>
                            <Table stickyHeader highlightOnHover striped withBorder miw={800}>
                                <Table.Thead>
                                    <Table.Tr className={"customerListTableDataRow"}>
                                        <Th
                                            sorted={sortBy === "name"}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting("name")}
                                        >
                                            <IconSignature className={"customerTableIcon"}/>
                                            <div className={"customerTableName"}>
                                                Name
                                            </div>
                                        </Th>
                                        <Th
                                            sorted={sortBy === "hamperCode"}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting("hamperCode")}
                                        >
                                            <IconScan className={"customerTableIcon"}/>
                                            <div className={"customerTableName"}>
                                                Hamper Code
                                            </div>
                                        </Th>
                                        <Th
                                            sorted={sortBy === "address"}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting("address")}
                                        >
                                            <IconMapPin className={"customerTableIcon"}/>
                                            <div className={"customerTableName"}>
                                                Address
                                            </div>
                                        </Th>
                                        <Th
                                            sorted={sortBy === "status"}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting("status")}
                                        >
                                            <IconProgressAlert className={"customerTableIcon"}/>
                                            <div className={"customerTableName"}>
                                                Status
                                            </div>
                                        </Th>
                                        <Th
                                            sorted={sortBy === "statusUpdatedAt"}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting("statusUpdatedAt")}
                                        >
                                            <IconClockHour3 className={"customerTableIcon"}/>
                                            <div className={"customerTableName"}>
                                                Status Updated
                                            </div>
                                        </Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody className={"customerListTableBody"}>{rows}</Table.Tbody>
                            </Table>
                        </ScrollArea>
                    )}
                </Paper>
            </Container>
        </>
    );
}

export default VerifiedCustomerHamperList;